import { LanguageVariantsSection } from "@/components/language-variants-section";
import { PaginationControls } from "@/components/pagination-controls";
import { MediaPreview } from "@/components/studio/file-upload";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { usePagination } from "@/hooks/use-pagination";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import {
  useAddCategoryTranslation,
  useArchiveCategory,
  useCategories,
  useCategoryTranslations,
  useCreateCategory,
  useDeleteCategoryTranslation,
  useRestoreCategory,
  useUpdateCategory,
  useUpdateCategoryTranslation,
} from "@/hooks/useCategories";
import { useAuth } from "@/lib/auth";
import { Category } from "@/lib/schema";
import { SUPPORTED_LANGUAGES, getLanguageLabel } from "@/lib/studio-constants";
import {
  Archive,
  ArrowLeft,
  CheckCircle2,
  FolderTree,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";

interface CategoryVariantEdit {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
}

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
  pending: { label: "Pending", variant: "outline" },
};

const COLOR_OPTIONS = [
  { value: "#FF68C3", label: "Rose" },
  { value: "#FFA98B", label: "Peach" },
  { value: "#A78BFA", label: "Lavender" },
  { value: "#34D399", label: "Sage" },
  { value: "#60A5FA", label: "Sky" },
  { value: "#FBBF24", label: "Sun" },
  { value: "#F87171", label: "Coral" },
  { value: "#818CF8", label: "Iris" },
];

export default function Categories() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formName, setFormName] = useState("");
  const [formColor, setFormColor] = useState<string>("");
  const [formThumbnailUrl, setFormThumbnailUrl] = useState<string>("");
  const [primaryLanguageCode, setPrimaryLanguageCode] = useState("");
  const { uploadFile, isUploading } = useUpload();
  const [variants, setVariants] = useState<CategoryVariantEdit[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const [variantsLoaded, setVariantsLoaded] = useState(false);

  const isAdmin =
    user?.roles.includes("super_admin") || user?.roles.includes("admin");

  const { data: categories = [], isLoading } = useCategories({
    search: searchQuery,
    status: statusFilter === "all" ? undefined : (statusFilter as any),
  });

  const selectedId = editingCategory?.id || null;

  const { data: existingVariants = [], isLoading: variantsLoading } =
    useCategoryTranslations(selectedId);

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const archiveCategory = useArchiveCategory();
  const restoreCategory = useRestoreCategory();
  const addTranslation = useAddCategoryTranslation();
  const updateTranslation = useUpdateCategoryTranslation();
  const deleteTranslation = useDeleteCategoryTranslation();

  useEffect(() => {
    if (selectedId && existingVariants.length > 0 && !variantsLoaded) {
      setVariants(
        existingVariants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle || "",
          localizedDescription: v.localizedDescription || "",
        })),
      );
      setVariantsLoaded(true);
    }
  }, [selectedId, existingVariants, variantsLoaded]);

  const availableLanguages = useMemo(
    () =>
      SUPPORTED_LANGUAGES.filter(
        (lang) => !variants.some((v) => v.languageCode === lang.code),
      ),
    [variants],
  );

  const activeVariant = variants[activeVariantIndex] ?? null;

  async function saveVariantsForCategory(
    categoryId: string,
    variantsToSave: CategoryVariantEdit[],
    existingVars: CategoryVariantEdit[],
  ) {
    const currentIds = new Set(variantsToSave.map((v) => v.id).filter(Boolean));

    // Handle deletions
    for (const ev of existingVars) {
      if (ev.id && !currentIds.has(ev.id)) {
        await deleteTranslation.mutateAsync({
          categoryId,
          translationId: ev.id,
        });
      }
    }

    // Handle updates and additions
    for (const v of variantsToSave) {
      if (v.id) {
        // Optimization: only update if changed (optional, but keep it simple for now)
        await updateTranslation.mutateAsync({
          categoryId,
          translationId: v.id,
          data: {
            localizedTitle: v.localizedTitle,
            localizedDescription: v.localizedDescription,
            isDefault: v.isDefault,
          },
        });
      } else {
        await addTranslation.mutateAsync({
          id: categoryId,
          data: {
            languageCode: v.languageCode,
            localizedTitle: v.localizedTitle,
            localizedDescription: v.localizedDescription,
            isDefault: v.isDefault,
          },
        });
      }
    }
  }

  const handleSave = async () => {
    if (!formName.trim()) return;

    const untitled = variants.find((v) => !v.localizedTitle.trim());
    if (untitled) {
      toast({
        title: `Add a title for the ${getLanguageLabel(untitled.languageCode)} variant`,
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory.mutateAsync({
          id: editingCategory.id,
          data: {
            title: formName,
            // EVO-63: null clears the field; undefined was dropped from the PATCH.
            color: formColor || null,
            thumbnailUrl: formThumbnailUrl || null,
            primaryLanguageCode: variants.find((v) => v.isDefault)?.languageCode,
          },
        });
        await saveVariantsForCategory(
          editingCategory.id,
          variants,
          existingVariants.map((variant) => ({
            id: variant.id,
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
            localizedTitle: variant.localizedTitle || "",
            localizedDescription: variant.localizedDescription || "",
          })),
        );
      } else {
        await createCategory.mutateAsync({
          title: formName,
          primaryLanguageCode: primaryLanguageCode || "en",
          color: formColor || undefined,
          thumbnailUrl: formThumbnailUrl || undefined,
          translations: variants.map((variant) => ({
            languageCode: variant.languageCode,
            isDefault: variant.isDefault,
            localizedTitle: variant.localizedTitle,
            localizedDescription: variant.localizedDescription,
          })),
        });
      }
      closeDialog();
    } catch (error) {
      // Error handling is managed by hooks/toast
    }
  };

  const filtered = categories; // Filtering handled by API now
  const { pageItems, ...paginationProps } = usePagination(filtered);

  const isSaving =
    createCategory.isPending ||
    updateCategory.isPending ||
    addTranslation.isPending ||
    updateTranslation.isPending ||
    deleteTranslation.isPending;

  function handlePrimaryLanguageChange(langCode: string) {
    setPrimaryLanguageCode(langCode);
    if (!editingCategory) {
      setVariants([
        {
          languageCode: langCode,
          isDefault: true,
          localizedTitle: "",
          localizedDescription: "",
        },
      ]);
      setActiveVariantIndex(0);
    }
  }

  function openCreateDialog() {
    setEditingCategory(null);
    setFormName("");
    setFormColor("");
    setFormThumbnailUrl("");
    setPrimaryLanguageCode("");
    setVariants([]);
    setActiveVariantIndex(0);
    setVariantsLoaded(false);
    setPendingRemoveIndex(null);
    setDialogOpen(true);
  }

  function openEditDialog(cat: Category) {
    setEditingCategory(cat);
    setFormName(cat.title);
    setFormColor(cat.color || "");
    setFormThumbnailUrl(cat.thumbnailUrl || "");
    setPrimaryLanguageCode(cat.primaryLanguageCode || "en");
    setVariants([]);
    setActiveVariantIndex(0);
    setVariantsLoaded(false);
    setPendingRemoveIndex(null);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormName("");
    setFormColor("");
    setFormThumbnailUrl("");
    setPrimaryLanguageCode("");
    setVariants([]);
    setActiveVariantIndex(0);
    setVariantsLoaded(false);
    setPendingRemoveIndex(null);
  }

  function addVariant(langCode: string) {
    if (variants.some((v) => v.languageCode === langCode)) return;
    const isFirst = variants.length === 0;
    setVariants((prev) => [
      ...prev,
      {
        languageCode: langCode,
        isDefault: isFirst,
        localizedTitle: "",
        localizedDescription: "",
      },
    ]);
    setActiveVariantIndex(variants.length);
  }

  function confirmRemoveVariant() {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex]?.isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  }

  function setVariantAsDefault(index: number) {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({
      ...v,
      isDefault: i === index,
    }));
    setVariants(updated);
    setActiveVariantIndex(index);
  }

  function updateVariantField(field: keyof CategoryVariantEdit, value: string) {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  }

  return (
    <div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      data-testid="studio-categories"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button
            variant="ghost"
            size="icon"
            data-testid="button-back-overview"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-categories-title"
          >
            Categories
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Global taxonomy layer used across all content types
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="evo-gradient text-white border-0"
          data-testid="button-create-category"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Category
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-categories"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card data-testid="list-categories-empty">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-3 rounded-full bg-primary/10 mb-4">
              <FolderTree className="h-8 w-8 text-primary" />
            </div>
            <p className="text-muted-foreground mb-4">
              {categories.length === 0
                ? "No categories created yet"
                : "No categories match your filters"}
            </p>
            {categories.length === 0 && (
              <Button
                variant="outline"
                onClick={openCreateDialog}
                data-testid="button-create-category-empty"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first category
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((cat) => (
                <TableRow key={cat.id} data-testid={`row-category-${cat.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {cat.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                      )}
                      <div>
                        <span className="font-medium text-foreground">
                          {cat.title}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        STATUS_CONFIG[cat.status]?.variant || "secondary"
                      }
                      data-testid={`badge-status-${cat.id}`}
                    >
                      {STATUS_CONFIG[cat.status]?.label || cat.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span
                      className="text-sm text-muted-foreground"
                      data-testid={`text-usage-${cat.id}`}
                    >
                      {cat.contentCount ?? 0} items
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {cat.createdByName || "System"}
                    </span>
                    <span className="text-xs text-muted-foreground/70 block">
                      {cat.createdByRole}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {new Date(cat.updatedAt).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`button-actions-${cat.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => openEditDialog(cat)}
                          data-testid={`action-edit-${cat.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {cat.status === "active" && isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => archiveCategory.mutate(cat.id)}
                              className="text-destructive focus:text-destructive"
                              data-testid={`action-archive-${cat.id}`}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          </>
                        )}
                        {cat.status === "archived" && isAdmin && (
                          <DropdownMenuItem
                            onClick={() => restoreCategory.mutate(cat.id)}
                            data-testid={`action-restore-${cat.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Restore
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        </Card>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}
      >
        <DialogContent
          className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="dialog-category-form"
        >
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingCategory ? "Edit Category" : "Create Category"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? "Update the category details below"
                : "Categories are shared across all content types"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {!editingCategory ? (
              <div className="space-y-2">
                <Label>Primary Language</Label>
                <Select
                  value={primaryLanguageCode}
                  onValueChange={handlePrimaryLanguageChange}
                >
                  <SelectTrigger data-testid="select-primary-language">
                    <SelectValue placeholder="Choose primary language..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Primary Language</Label>
                <p className="text-sm text-muted-foreground">
                  {getLanguageLabel(primaryLanguageCode)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="category-name">Name *</Label>
              <Input
                id="category-name"
                placeholder="e.g. Meditation, Breathwork, Self-Care"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                data-testid="input-category-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Colour (optional)</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    title={opt.label}
                    className={`w-8 h-8 rounded-md border-2 transition-all ${
                      formColor === opt.value
                        ? "border-foreground scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: opt.value }}
                    onClick={() =>
                      setFormColor(formColor === opt.value ? "" : opt.value)
                    }
                    data-testid={`color-${opt.value}`}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Thumbnail Image (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Background image for the category pill on the mobile homepage
              </p>
              {formThumbnailUrl ? (
                <MediaPreview
                  url={formThumbnailUrl}
                  mediaType="image"
                  onRemove={() => setFormThumbnailUrl("")}
                />
              ) : (
                <UploadDropzone
                  type="image"
                  label="Upload thumbnail"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const result = await uploadFile(file);
                    if (result) setFormThumbnailUrl(result.objectPath);
                  }}
                  isUploading={isUploading}
                  testId="upload-category-thumbnail"
                />
              )}
            </div>

            <LanguageVariantsSection
              variants={variants}
              activeVariantIndex={activeVariantIndex}
              onActiveVariantChange={setActiveVariantIndex}
              availableLanguages={availableLanguages}
              onAddVariant={addVariant}
              onSetDefault={setVariantAsDefault}
              pendingRemoveIndex={pendingRemoveIndex}
              onRequestRemove={setPendingRemoveIndex}
              onConfirmRemove={confirmRemoveVariant}
              onCancelRemove={() => setPendingRemoveIndex(null)}
            >
              {variants[activeVariantIndex] && (
                <div
                  className="space-y-4 pt-2"
                  data-testid="category-variant-fields"
                >
                  <div className="space-y-2">
                    <Label>Localized Title</Label>
                    <Input
                      value={variants[activeVariantIndex].localizedTitle}
                      onChange={(e) =>
                        updateVariantField("localizedTitle", e.target.value)
                      }
                      placeholder={`Title in ${getLanguageLabel(variants[activeVariantIndex].languageCode)}`}
                      data-testid={`input-category-variant-title-${variants[activeVariantIndex].languageCode}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={variants[activeVariantIndex].localizedDescription}
                      onChange={(e) =>
                        updateVariantField(
                          "localizedDescription",
                          e.target.value,
                        )
                      }
                      placeholder={`Description in ${getLanguageLabel(variants[activeVariantIndex].languageCode)}`}
                      rows={2}
                      data-testid={`input-category-variant-description-${variants[activeVariantIndex].languageCode}`}
                    />
                  </div>
                </div>
              )}
            </LanguageVariantsSection>

            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={closeDialog}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  !formName.trim() ||
                  (!editingCategory && !primaryLanguageCode) ||
                  isSaving ||
                  (!!editingCategory && variantsLoading)
                }
                data-testid="button-save"
              >
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCategory ? "Save Changes" : "Create Category"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
